/**
 * @file gantt.component.tsx
 * @description Компонент диаграммы Ганта для визуализации временных планов проектов в Task Manager приложении. Использует DHX Gantt библиотеку с поддержкой маркеров времени, настраиваемых колонок, выходных дней, спринтов и кастомных tooltip'ов. Предоставляет readonly режим просмотра с возможностью изменения размера колонок и выделения строк.
 * @author AI-Generated | Human-Reviewed
 * @created 2025-09-19
 * @updated 2025-09-19
 * @dependencies ['react-dom/client', 'dhx-gantt', 'moment-timezone', 'react', '@utils/date-time-helper']
 * @related-files ['src/components/ui/visualizations/charts/gantt/templates/tooltip.tsx']
 */

import { createRoot } from 'react-dom/client';
import { Gantt as DhxGantt, GanttStatic, GridColumn, Scale, ScaleArray } from 'dhx-gantt';
import 'dhx-gantt/codebase/dhtmlxgantt.css';
import moment from 'moment-timezone';
import { isValidElement, useEffect, useRef, useState } from 'react';
import { customTooltip } from './templates/tooltip';
import { dateToTimestamp, timestampToDate } from '@utils/date-time-helper';
import type { Task, IUnworkedDay, IGanttData, IGanttExtra } from '@src/types/gantt';

/** Свойства компонента Gantt */
interface IGanttProps {
    /** Данные задач для отображения */
    data: IGanttData;
    /** Конфигурация колонок */
    columns?: GridColumn[];
    /** Дополнительные данные (спринты, выходные) */
    extra?: IGanttExtra;
    /** Обработчик изменения размера колонки */
    onColumnResize?: (column: GridColumn, newWidth: number) => void;
}

export const Gantt = ({ data, columns, extra, onColumnResize }: IGanttProps) => {
    const container = useRef<HTMLDivElement>(null);
    const [markerTime, setMarkerTime] = useState(moment().format('HH:mm'));
    const lastMarkerRef = useRef<string | number | null>(null);
    const [ganttComponent, setGanttComponent] = useState<GanttStatic | null>(null);
    const [selectedRow, setSelectedRow] = useState<string | number | null>(null);

    const initGantt = () => {
        const gantt: GanttStatic = DhxGantt.getGanttInstance();
        gantt.i18n.setLocale('ru');

        gantt.plugins({
            marker: true,
            drag_timeline: true,
            click_drag: false,
            tooltip: false
        });

        const weekendDays = extra?.unworkedDays ? extra.unworkedDays.map((weekend: IUnworkedDay) => weekend.date) : [];

        gantt.config.readonly = true;
        gantt.config.deepcopy_on_parse = true;
        gantt.config.drag_resize = false;
        gantt.config.drag_progress = false;
        gantt.config.grid_resize = true;
        gantt.config.open_tree_initially = true;
        gantt.config.min_column_width = 50;
        gantt.config.link_line_width = 1;
        gantt.config.scale_height = 60;
        gantt.config.min_column_width = 32;
        gantt.config.row_height = 29;
        gantt.config.scales = getScales(weekendDays);
        gantt.config.columns = getColumns(columns);
        gantt.config.drag_timeline = {
            ignore: '.gantt_split_child',
            useKey: false
        };
        gantt.config.external_render = {
            // checks the element is a React element
            isElement: (element) => {
                return isValidElement(element);
            },
            // renders the React element into the DOM
            renderElement: (element, container) => {
                createRoot(container).render(element);
            }
        };

        //-----------------templates
        gantt.templates.timeline_cell_class = (_item, date) => {
            if (date.getDay() == 0 || date.getDay() == 6) {
                return 'weekend';
            }

            if (weekendDays.includes(dateToTimestamp(date))) {
                return 'weekend';
            }
        };
        gantt.templates.grid_row_class = (_start, _end, task) => {
            if (task.type == gantt.config.types.project) {
                return 'project-line';
            }
        };
        gantt.templates.task_text = () => '';
        gantt.templates.parse_date = (date: number | string) => timestampToDate(date as number);

        // gantt.templates.tooltip_text = (_start, _end, task) => {
        //     if (!task.$item?.$type || task.$item?.$type !== 'Task') {
        //         return '';
        //     }

        //     return `<span class="nowrap"><b>Задача:</b> ${task.$item.name}</span>`;
        // };
        gantt.templates.tooltip_text = (_start: Date, _end: Date, task: Task) => {
            if (!task.$item?.$type || task.$item?.$type !== 'Task') {
                return '';
            }

            return customTooltip(task);
        };

        //-----------------templates

        gantt.config.type_renderers['project'] = (task: Task) => {
            const main_el = document.createElement('div');
            main_el.setAttribute(gantt.config.task_attribute, String(task.id));
            const size = gantt.getTaskPosition(task);
            main_el.innerHTML = ["<div class='project-left'></div>", "<div class='project-right'></div>"].join('');
            main_el.className = 'custom-project';

            main_el.style.left = size.left + 'px';
            main_el.style.top = size.top + 7 + 'px';
            main_el.style.width = size.width + 'px';

            return main_el;
        };

        gantt.attachEvent('onColumnResizeEnd', (_index: number, column: GridColumn, new_width: number) => {
            onColumnResize?.(column, new_width);

            return true;
        });

        gantt.attachEvent('onTaskSelected', (id: string | number) => {
            setSelectedRow(id);

            return true;
        });

        const interval = setInterval(() => {
            setMarkerTime(moment().format('HH:mm'));
        }, 1000);

        lastMarkerRef.current = gantt.addMarker({
            start_date: new Date(),
            css: 'gantt_marker gantt_marker-today',
            text: 'NOW'
        });

        if (extra && extra.sprints?.length) {
            extra.sprints.map((sprint: Task) => {
                // Используем end_date для спринтов из DHX Gantt типа Task
                if (sprint.end_date) {
                    gantt.addMarker({
                        start_date: sprint.end_date,
                        css: 'gantt_marker gantt_marker-end-spring',
                        text: 'Final sprint'
                    });
                }
            });
        }

        if (container.current) {
            gantt.init(container.current);
            gantt.parse(data);
            setGanttComponent(gantt);
        }

        return () => {
            clearInterval(interval);
        };
    };

    useEffect(() => {
        if (!container.current) {
            return;
        }

        initGantt();

        return () => {
            ganttComponent && ganttComponent.destructor();
            container.current?.innerHTML ? (container.current.innerHTML = '') : false;
        };
    }, []);

    useEffect(() => {
        renderCurrentTimeMarker();
    }, [markerTime]);

    useEffect(() => {
        if (!ganttComponent) {
            return;
        }

        ganttComponent.destructor();
        initGantt();
    }, [columns]);

    useEffect(() => {
        if (!ganttComponent) {
            return;
        }

        ganttComponent.clearAll();
        ganttComponent.parse(data);
        ganttComponent.render();

        if (selectedRow) {
            if (data?.data.find((row: Task) => row.id === selectedRow)) {
                ganttComponent.selectTask(selectedRow);
                ganttComponent.showTask(selectedRow);
            } else {
                setSelectedRow(null);
            }
        }
    }, [data]);

    const renderCurrentTimeMarker = () => {
        if (!ganttComponent) {
            return;
        }

        if (lastMarkerRef.current && ganttComponent.getMarker(lastMarkerRef.current)) {
            ganttComponent.getMarker(lastMarkerRef.current).start_date = new Date();
            ganttComponent.updateMarker(lastMarkerRef.current);
            return;
        }
    };

    return <div className="gantt__chart" ref={container} style={{ width: '100%', height: '100%' }}></div>;
};

const getScales = (extraWeekends: number[]): ScaleArray<Scale> => {
    return [
        { unit: 'month', step: 1, format: '%F, %Y' },
        {
            unit: 'day',
            step: 1,
            format: '%d',
            css: (date) => {
                if (date.getDay() === 0 || date.getDay() === 6) {
                    return 'weekend';
                }

                if (extraWeekends.includes(dateToTimestamp(date))) {
                    return 'weekend';
                }

                return '';
            }
        }
    ];
};

const getColumns = (columns?: GridColumn[]): GridColumn[] => {
    if (columns) {
        return columns;
    }

    return [
        { name: 'text', label: 'Task name', tree: true, resize: true, width: 160 },
        { name: 'start_date', label: 'Start time', resize: true, width: 100 },
        { name: 'duration', label: 'Duration', resize: true, width: 50 }
    ];
};
